/**
 * sanitizeHtml — the single HTML sanitizer for every rich-text string the
 * app stores (Tasks Kit description field today; any future `RichText` kit
 * consumer). Built on DOMPurify (plan-approved — no sanitizer existed in the
 * repo before this; confirmed via a `package.json` grep).
 *
 * The allow-list is fitted to what `ui/rich-text.tsx` (TipTap) can produce
 * PLUS what the legacy hand-rolled `RichTextEditor` the former Tasks page
 * had (`document.execCommand`, raw contenteditable) could already have left
 * in SAVED rows — browsers' native `execCommand`/paste path can emit `<div>`,
 * `<span>`, `<font>` wrappers that never went through a sanitizer. Those
 * tags are kept (so existing descriptions don't lose text on load) but every
 * attribute except a safe link set is stripped — DOMPurify's default
 * behaviour for a disallowed tag is to drop the tag and keep its text
 * content, which is exactly the "keep text, drop styling" contract asked
 * for here.
 *
 * This module is renderer-only — it is never imported from the main
 * process — so the bare `dompurify` import always finds a real `window` to
 * bind to. Tests exercise it under `// @vitest-environment jsdom` so
 * `window` exists there too.
 */
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'a',
  // Legacy execCommand/paste wrappers (the old Tasks page editor never
  // sanitized paste) — kept for load fidelity, attributes stripped below.
  'div',
  'span',
  'font',
];

const ALLOWED_ATTR = ['href', 'target', 'rel'];

// Mirrors DOMPurify's own default ALLOWED_URI_REGEXP (which allow-lists
// http(s)/mailto/tel/etc.), narrowed to the three schemes a link in this
// app is ever allowed to carry. The colon MUST be in the final negated
// class — omitting it would let "javascript:" and "data:" slip through the
// third (schemeless-prefix) branch.
const SAFE_URI_REGEXP = /^(?:(?:https?|mailto):|[^a-z]|[a-z0-9+.-]+(?:[^a-z0-9+.:-]|$))/i;

let hookInstalled = false;
function ensureLinkHook() {
  if (hookInstalled) return;
  hookInstalled = true;
  // Forces a safe `rel` on every surviving link regardless of what the
  // source HTML carried (or omitted) — belt-and-braces alongside the
  // allow-listed `target`/`href`.
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

/**
 * Sanitizes a rich-text HTML string for storage/display. Strips scripts,
 * event-handler attributes, inline styles, iframes, and any tag/attribute
 * outside the allow-list above; keeps a disallowed tag's text. Forces a safe
 * `rel` and scheme (http/https/mailto) on links. Empty/undefined input
 * returns `''`.
 */
export function sanitizeRichText(html: string): string {
  if (!html) return '';
  ensureLinkHook();
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
    // DOMPurify defaults both of these to `true`, which would let any
    // `data-*`/`aria-*` attribute through regardless of ALLOWED_ATTR — the
    // allow-list above is meant to be authoritative.
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
  return clean.trim();
}

/** Same scheme policy `sanitizeRichText` enforces on `href`, exposed so the
 *  Link toolbar control (ui/rich-text.tsx) can reject an unsafe URL at entry
 *  time rather than relying only on the post-hoc sanitize pass. */
export function isSafeUrl(url: string): boolean {
  if (!url) return false;
  return SAFE_URI_REGEXP.test(url);
}
